// ANYCAST Web Audio engine: eight synthesised voices, an evolving drone, soft percussion,
// a generated hall reverb, a tempo-synced ping-pong delay and a master chain that cannot clip.
//
// The graph is built on a BaseAudioContext so the very same code renders on an
// OfflineAudioContext (see AudioEngine.renderOffline) for verification.

import type { VoiceName, Harmony } from "../../shared/music";
import { STEP_MS, VOICES, midiToFreq } from "../../shared/music";
import { RECIPES, VOICE_MIX, type NoteBuild } from "./voices";
import { makeNoiseBuffer, makePingPong, makeReverbIR, makeSoftClipCurve } from "./fx";

export interface NoteOptions {
  voice: VoiceName;
  midi: number;
  /** AudioContext time, seconds. In the past means "as soon as possible". */
  when: number;
  /** 0..1 */
  vel: number;
  /** -1..1 */
  pan?: number;
  /** 0..1; older notes are quieter and darker. */
  age?: number;
  accent?: boolean;
}

export interface AudioEngineOptions {
  /** Build on an existing context (e.g. an OfflineAudioContext). The engine counts as started. */
  context?: BaseAudioContext;
  /** Maximum simultaneously sounding notes before the oldest is stolen. Default 48. */
  maxVoices?: number;
}

interface ActiveNote {
  out: GainNode;
  build: NoteBuild;
  start: number;
  alive: boolean;
}

interface PadLayer {
  gain: GainNode;
  nodes: AudioNode[];
  oscs: OscillatorNode[];
}

const clamp = (x: number, lo: number, hi: number) => (x < lo ? lo : x > hi ? hi : x);
const dbToGain = (db: number) => Math.pow(10, db / 20);
const finite = (x: number, fallback: number) => (Number.isFinite(x) ? x : fallback);

/** Dotted eighth: 3 sixteenth steps. */
export const DELAY_TIME = (3 * STEP_MS) / 1000;
/** Recommended scheduling lookahead for hosts, seconds. */
export const RECOMMENDED_LOOKAHEAD = 0.12;

class Graph {
  readonly ctx: BaseAudioContext;
  readonly noise: AudioBuffer;
  readonly voiceBus = {} as Record<VoiceName, GainNode>;
  readonly dry: GainNode;
  readonly reverbSend: GainNode;
  readonly delaySend: GainNode;
  readonly padBus: GainNode;
  readonly padFilter: BiquadFilterNode;
  readonly sub: OscillatorNode;
  readonly subGain: GainNode;
  readonly percBus: GainNode;
  readonly master: GainNode;
  readonly mute: GainNode;
  readonly out: AudioNode;
  readonly analyser: AnalyserNode;

  constructor(ctx: BaseAudioContext) {
    this.ctx = ctx;
    this.noise = makeNoiseBuffer(ctx, 2.5);
    const g = (v: number) => {
      const n = ctx.createGain();
      n.gain.value = v;
      return n;
    };
    const biquad = (type: BiquadFilterType, f: number, q = 0.707, gainDb = 0) => {
      const b = ctx.createBiquadFilter();
      b.type = type;
      b.frequency.value = f;
      b.Q.value = q;
      b.gain.value = gainDb;
      return b;
    };

    // Busses.
    const sum = g(1);
    this.dry = g(1);
    this.reverbSend = g(1);
    this.delaySend = g(1);
    this.dry.connect(sum);

    // Reverb: highpassed input (keeps the lows out of the wash), generated IR.
    const verb = ctx.createConvolver();
    verb.normalize = false;
    verb.buffer = makeReverbIR(ctx, 4.5);
    const verbHp = biquad("highpass", 190);
    const verbLp = biquad("lowpass", 7500);
    const verbReturn = g(0.55);
    this.reverbSend.connect(verbHp).connect(verb).connect(verbLp).connect(verbReturn).connect(sum);

    // Ping-pong delay, feeding a little of itself into the reverb.
    const pp = makePingPong(ctx, DELAY_TIME, 0.35);
    const delayReturn = g(0.5);
    this.delaySend.connect(pp.input);
    pp.output.connect(delayReturn).connect(sum);
    const delayToVerb = g(0.35);
    pp.output.connect(delayToVerb).connect(this.reverbSend);

    // Voice busses (per voice sends).
    for (const v of VOICES) {
      const bus = g(1);
      const mix = VOICE_MIX[v];
      bus.connect(this.dry);
      bus.connect(g(mix.reverb)).connect(this.reverbSend);
      bus.connect(g(mix.delay)).connect(this.delaySend);
      this.voiceBus[v] = bus;
    }

    // Drone / pad bed.
    this.padBus = g(0);
    this.padFilter = biquad("lowpass", 520, 0.6);
    const padOut = g(1);
    this.padBus.connect(this.padFilter).connect(padOut);
    padOut.connect(this.dry);
    padOut.connect(g(0.9)).connect(this.reverbSend);
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.045;
    const lfoDepth = g(240);
    lfo.connect(lfoDepth).connect(this.padFilter.frequency);
    lfo.start();
    const lfo2 = ctx.createOscillator();
    lfo2.frequency.value = 0.11;
    const lfo2Depth = g(90);
    lfo2.connect(lfo2Depth).connect(this.padFilter.frequency);
    lfo2.start();
    this.sub = ctx.createOscillator();
    this.sub.type = "sine";
    this.sub.frequency.value = 65.4;
    this.subGain = g(0);
    this.sub.connect(this.subGain).connect(this.dry);
    this.sub.start();

    // Percussion.
    this.percBus = g(0.7);
    this.percBus.connect(this.dry);
    this.percBus.connect(g(0.12)).connect(this.reverbSend);

    // Master chain: cleanup EQ -> glue -> limiter -> trim -> master -> mute -> soft clip.
    const hp = biquad("highpass", 35, 0.7);
    const shelf = biquad("highshelf", 8500, 0.707, -3);
    const mud = biquad("peaking", 280, 0.8, -1.5);
    const glue = ctx.createDynamicsCompressor();
    glue.threshold.value = -20;
    glue.knee.value = 12;
    glue.ratio.value = 2.2;
    glue.attack.value = 0.025;
    glue.release.value = 0.3;
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -3;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.001;
    limiter.release.value = 0.12;
    // Chrome's compressors add automatic make-up gain; trim it back so the limiter can breathe.
    const trim = g(0.85);
    this.master = g(0.8);
    this.mute = g(1);
    const pre = g(0.5); // soft clip curve domain is [-2, 2]
    const clip = ctx.createWaveShaper();
    clip.curve = makeSoftClipCurve();
    clip.oversample = "2x";
    sum.connect(hp).connect(mud).connect(shelf).connect(glue).connect(limiter).connect(trim);
    trim.connect(this.master).connect(this.mute).connect(pre).connect(clip);
    this.out = clip;
    clip.connect(ctx.destination);
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = 0.8;
    clip.connect(this.analyser);
  }
}

export class AudioEngine {
  private g: Graph | null = null;
  private owned: AudioContext | null = null;
  private starting: Promise<void> | null = null;
  private readonly maxVoices: number;
  private active: ActiveNote[] = [];
  private recent: number[] = [];
  private pad: PadLayer | null = null;
  private harmonyKey = "";
  private droneLevel = 1;
  private levelSmooth = 0;
  private timeBuf: Float32Array<ArrayBuffer> | null = null;
  private recordDest: MediaStreamAudioDestinationNode | null = null;
  private masterVolume = 0.8;

  constructor(opts: AudioEngineOptions = {}) {
    this.maxVoices = opts.maxVoices ?? 48;
    if (opts.context) this.g = new Graph(opts.context);
  }

  /** Render a script offline (for tests/verification). The script schedules at context times from 0. */
  static async renderOffline(
    seconds: number,
    script: (engine: AudioEngine) => void | Promise<void>,
    sampleRate = 44100,
  ): Promise<AudioBuffer> {
    const ctx = new OfflineAudioContext(2, Math.ceil(seconds * sampleRate), sampleRate);
    const engine = new AudioEngine({ context: ctx });
    await script(engine);
    return ctx.startRendering();
  }

  /** Create/resume the AudioContext. Call from a user gesture. Idempotent. */
  start(): Promise<void> {
    if (this.g && !this.owned) return Promise.resolve();
    if (!this.starting) {
      this.starting = (async () => {
        if (!this.owned) {
          const Ctor: typeof AudioContext =
            window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
          this.owned = new Ctor({ latencyHint: "interactive" });
          this.g = new Graph(this.owned);
          this.applyMaster(0);
        }
        if (this.owned.state !== "running") await this.owned.resume();
      })().catch((err: unknown) => {
        this.starting = null; // allow a retry from the next gesture
        throw err;
      });
    }
    return this.starting;
  }

  get started(): boolean {
    return this.g !== null;
  }

  get ctx(): AudioContext | null {
    return this.owned;
  }

  /** The underlying context, including an offline one. */
  get context(): BaseAudioContext | null {
    return this.g?.ctx ?? null;
  }

  get currentTime(): number {
    return this.g?.ctx.currentTime ?? 0;
  }

  get outputLatency(): number {
    const c = this.owned;
    if (!c) return 0;
    return c.outputLatency || c.baseLatency || 0;
  }

  /** Number of notes currently sounding (after polyphony guard). */
  get activeVoices(): number {
    return this.active.length;
  }

  playNote(o: NoteOptions): void {
    const g = this.g;
    if (!g) return;
    const recipe = RECIPES[o.voice];
    if (!recipe) return;
    const ctx = g.ctx;
    const now = ctx.currentTime;
    const t = Math.max(finite(o.when, now), now + 0.005);
    const midi = clamp(finite(o.midi, 60), 12, 120);
    const vel = clamp(finite(o.vel, 0.7), 0, 1) * (o.accent ? 1.15 : 1);
    if (vel <= 0.001) return;
    const age = clamp(finite(o.age ?? 0, 0), 0, 1);
    const pan = clamp(finite(o.pan ?? 0, 0), -1, 1);

    this.reap(now);
    // Polyphony guard, evaluated at the note's own start time (works for lookahead and offline).
    let sounding = 0;
    let victim: ActiveNote | null = null;
    for (const n of this.active) {
      if (n.start <= t + 0.001 && n.build.end > t) {
        sounding++;
        if (!victim || n.start < victim.start) victim = n;
      }
    }
    if (sounding >= this.maxVoices && victim) {
      // Too crowded: quietly skip soft, old notes; steal the oldest sounding note for the rest.
      if (vel < 0.35 || age > 0.8) return;
      this.release(victim, t);
      this.active.splice(this.active.indexOf(victim), 1);
    }

    // Crowd protection: when many notes start around the same time, each gets quieter.
    this.recent = this.recent.filter((x) => Math.abs(x - t) < 0.25);
    this.recent.push(t);
    const crowd = this.recent.length;
    const density = crowd <= 6 ? 1 : Math.sqrt(6 / crowd);

    // Pitch taming: roll off extreme highs and lows.
    const pitchDb = (midi > 79 ? -(midi - 79) * 0.3 : 0) + (midi < 43 ? -(43 - midi) * 0.35 : 0);
    const velAmp = 0.12 + 0.88 * Math.pow(Math.min(vel, 1), 1.6);
    const ageAmp = 1 - 0.55 * age;
    const level = VOICE_MIX[o.voice].level * velAmp * ageAmp * density * dbToGain(pitchDb) * (o.accent ? 1.12 : 1);
    const bright = clamp(0.25 + 0.75 * vel - 0.45 * age + (o.accent ? 0.15 : 0), 0.05, 1);

    const out = ctx.createGain();
    out.gain.value = level;
    const tone = ctx.createBiquadFilter();
    tone.type = "lowpass";
    const pitchCut = 16000 * Math.pow(2, -Math.max(0, midi - 72) / 18);
    tone.frequency.value = clamp(pitchCut * Math.pow(2, -3.2 * age), 600, ctx.sampleRate * 0.45);
    tone.Q.value = 0.5;
    const panner = ctx.createStereoPanner();
    panner.pan.value = pan;
    out.connect(tone).connect(panner).connect(g.voiceBus[o.voice]);

    const build = recipe({ ctx, noise: g.noise }, out, midiToFreq(midi), t, Math.min(vel, 1), bright);
    const note: ActiveNote = { out, build, start: t, alive: true };
    this.active.push(note);

    let pending = build.sources.length;
    const done = () => {
      if (--pending > 0) return;
      note.alive = false;
      for (const n of build.nodes) n.disconnect();
      out.disconnect();
      tone.disconnect();
      panner.disconnect();
      const i = this.active.indexOf(note);
      if (i >= 0) this.active.splice(i, 1);
    };
    for (const s of build.sources) s.onended = done;
  }

  private reap(now: number): void {
    if (this.active.length && this.active.some((n) => n.build.end < now - 0.5)) {
      this.active = this.active.filter((n) => n.build.end >= now - 0.5);
    }
  }

  private release(n: ActiveNote, now: number): void {
    const t = Math.max(now, n.start);
    n.out.gain.cancelScheduledValues(t);
    n.out.gain.setValueAtTime(n.out.gain.value, t);
    n.out.gain.setTargetAtTime(0, t, 0.02);
    for (const s of n.build.sources) {
      try {
        s.stop(t + 0.15);
      } catch {
        /* already stopped */
      }
    }
  }

  /** Retune the drone/pad to a new root/mode, crossfading over ~2 s from `when`. */
  setHarmony(h: Harmony, when: number): void {
    const g = this.g;
    if (!g) return;
    const key = `${h.root}:${h.scale.join(",")}`;
    if (key === this.harmonyKey) return;
    const first = this.harmonyKey === "";
    this.harmonyKey = key;
    const ctx = g.ctx;
    const t = Math.max(finite(when, ctx.currentTime), ctx.currentTime + 0.01);
    const fadeIn = first ? 3 : 2;

    let base = 48 + (((h.root % 12) + 12) % 12);
    if (base > 53) base -= 12;
    const pitches: [number, number][] = [
      [base, 1],
      [base + 7, 0.7],
    ];
    const third = h.scale.includes(4) ? 4 : h.scale.includes(3) ? 3 : null;
    if (third !== null) pitches.push([base + 12 + third, 0.4]);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(1, t + fadeIn);
    gain.connect(g.padBus);
    const nodes: AudioNode[] = [gain];
    const oscs: OscillatorNode[] = [];
    const detunes = [-7, 0.5, 6.5];
    for (const [midi, w] of pitches) {
      const f = midiToFreq(midi);
      for (let i = 0; i < detunes.length; i++) {
        const o = ctx.createOscillator();
        o.type = i === 1 ? "triangle" : "sawtooth";
        o.frequency.value = f;
        o.detune.value = detunes[i] ?? 0;
        const og = ctx.createGain();
        og.gain.value = (w * (i === 1 ? 0.5 : 0.28)) / 3;
        o.connect(og).connect(gain);
        o.start(t);
        nodes.push(o, og);
        oscs.push(o);
      }
    }
    const old = this.pad;
    if (old) {
      old.gain.gain.cancelScheduledValues(t);
      old.gain.gain.setValueAtTime(old.gain.gain.value, t);
      old.gain.gain.linearRampToValueAtTime(0, t + 2.2);
      const stopAt = t + 2.3;
      for (const o of old.oscs) o.stop(stopAt);
      const lead = old.oscs[0];
      if (lead) lead.onended = () => old.nodes.forEach((n) => n.disconnect());
    }
    this.pad = { gain, nodes, oscs };

    g.sub.frequency.setTargetAtTime(midiToFreq(base - 12), t, 0.6);
    this.applyDrone(t);
  }

  /** 0..1; the host lowers the drone when many notes are playing. */
  setDroneLevel(level: number): void {
    this.droneLevel = clamp(finite(level, 1), 0, 1);
    if (this.g) this.applyDrone(this.g.ctx.currentTime);
  }

  private applyDrone(t: number): void {
    const g = this.g;
    if (!g || !this.harmonyKey) return;
    const l = this.droneLevel;
    g.padBus.gain.setTargetAtTime(0.045 * l, t, 0.8);
    g.subGain.gain.setTargetAtTime(0.035 * l, t, 0.8);
  }

  /** Called once per 16th step (0..15) with intensity 0..1. Soft, organic, low in the mix. */
  percussion(step: number, when: number, intensity: number): void {
    const g = this.g;
    if (!g) return;
    const k = clamp(finite(intensity, 0), 0, 1);
    if (k <= 0.01) return;
    const ctx = g.ctx;
    const t = Math.max(finite(when, ctx.currentTime), ctx.currentTime + 0.005);
    const s = ((Math.floor(step) % 16) + 16) % 16;
    const human = () => 0.8 + Math.random() * 0.2;

    if ((s === 0 || s === 8) && k > 0.3) this.kick(t, (s === 0 ? 1 : 0.8) * k * human());
    if (s % 4 === 2) this.hat(t, 0.55 * k * human(), 0.07);
    else if (s % 2 === 1 && k > 0.5 && Math.random() < (k - 0.4) * 0.7) this.hat(t, 0.22 * k * human(), 0.035);
    if ((s === 4 || s === 12 || s === 14) && Math.random() < 0.18 * k) this.rim(t, 0.5 * k * human());
  }

  /** Runs a percussion builder; every node it tracks is disconnected once all its sources end. */
  private percVoice(build: (out: GainNode, track: (...n: AudioNode[]) => void) => AudioScheduledSourceNode[]): void {
    const g = this.g;
    if (!g) return;
    const out = g.ctx.createGain();
    out.connect(g.percBus);
    const nodes: AudioNode[] = [out];
    const sources = build(out, (...n) => nodes.push(...n));
    let pending = sources.length;
    for (const s of sources) {
      s.onended = () => {
        if (--pending > 0) return;
        for (const n of nodes) n.disconnect();
      };
    }
  }

  private kick(t: number, amt: number): void {
    const g = this.g;
    if (!g) return;
    const ctx = g.ctx;
    this.percVoice((out, track) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.setValueAtTime(120, t);
      o.frequency.exponentialRampToValueAtTime(48, t + 0.07);
      const env = ctx.createGain();
      env.gain.setValueAtTime(0, t);
      env.gain.linearRampToValueAtTime(0.55 * amt, t + 0.006);
      env.gain.setTargetAtTime(0, t + 0.006, 0.11);
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 220;
      o.connect(env).connect(lp).connect(out);
      o.start(t);
      o.stop(t + 0.9);
      // Felt: a soft thump of lowpassed noise.
      const n = ctx.createBufferSource();
      n.buffer = g.noise;
      const nf = ctx.createBiquadFilter();
      nf.type = "lowpass";
      nf.frequency.value = 900;
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0, t);
      ng.gain.linearRampToValueAtTime(0.12 * amt, t + 0.002);
      ng.gain.setTargetAtTime(0, t + 0.002, 0.012);
      n.connect(nf).connect(ng).connect(out);
      n.start(t, Math.random() * 1.5);
      n.stop(t + 0.15);
      track(o, env, lp, n, nf, ng);
      return [o, n];
    });
  }

  private hat(t: number, amt: number, decay: number): void {
    const g = this.g;
    if (!g) return;
    const ctx = g.ctx;
    this.percVoice((out, track) => {
      const n = ctx.createBufferSource();
      n.buffer = g.noise;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 5500;
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 8000 + Math.random() * 2000;
      bp.Q.value = 0.8;
      const env = ctx.createGain();
      env.gain.setValueAtTime(0, t);
      // Brushed: a 6 ms swell rather than a hard tick.
      env.gain.linearRampToValueAtTime(0.1 * amt, t + 0.006);
      env.gain.setTargetAtTime(0, t + 0.006, decay / 3);
      const pan = ctx.createStereoPanner();
      pan.pan.value = 0.25 + (Math.random() - 0.5) * 0.3;
      n.connect(hp).connect(bp).connect(env).connect(pan).connect(out);
      n.start(t, Math.random() * 1.5);
      n.stop(t + decay * 3 + 0.02);
      track(n, hp, bp, env, pan);
      return [n];
    });
  }

  private rim(t: number, amt: number): void {
    const g = this.g;
    if (!g) return;
    const ctx = g.ctx;
    this.percVoice((out, track) => {
      const o = ctx.createOscillator();
      o.type = "triangle";
      o.frequency.value = 820;
      const og = ctx.createGain();
      og.gain.setValueAtTime(0, t);
      og.gain.linearRampToValueAtTime(0.1 * amt, t + 0.001);
      og.gain.setTargetAtTime(0, t + 0.001, 0.012);
      const n = ctx.createBufferSource();
      n.buffer = g.noise;
      const bp = ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 1750;
      bp.Q.value = 3;
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0, t);
      ng.gain.linearRampToValueAtTime(0.14 * amt, t + 0.001);
      ng.gain.setTargetAtTime(0, t + 0.001, 0.01);
      const pan = ctx.createStereoPanner();
      pan.pan.value = -0.3;
      o.connect(og).connect(pan);
      n.connect(bp).connect(ng).connect(pan);
      pan.connect(out);
      o.start(t);
      o.stop(t + 0.15);
      n.start(t, Math.random() * 1.5);
      n.stop(t + 0.15);
      track(o, og, n, bp, ng, pan);
      return [o, n];
    });
  }

  /** 0..1 with a smooth ramp. */
  setMasterVolume(v: number): void {
    this.masterVolume = clamp(finite(v, 0.8), 0, 1);
    this.applyMaster(0.08);
  }

  private applyMaster(tau: number): void {
    const g = this.g;
    if (!g) return;
    const t = g.ctx.currentTime;
    // Perceptual-ish taper.
    const v = this.masterVolume * this.masterVolume;
    if (tau <= 0) g.master.gain.setValueAtTime(v, t);
    else g.master.gain.setTargetAtTime(v, t, tau);
  }

  setMuted(m: boolean): void {
    const g = this.g;
    if (!g) return;
    g.mute.gain.setTargetAtTime(m ? 0 : 1, g.ctx.currentTime, 0.05);
  }

  /** Smoothed RMS-ish output level 0..1 for visuals. */
  getLevel(): number {
    const g = this.g;
    if (!g) return 0;
    const a = g.analyser;
    if (!this.timeBuf || this.timeBuf.length !== a.fftSize) this.timeBuf = new Float32Array(a.fftSize);
    a.getFloatTimeDomainData(this.timeBuf);
    let e = 0;
    for (let i = 0; i < this.timeBuf.length; i++) {
      const x = this.timeBuf[i] ?? 0;
      e += x * x;
    }
    const rms = Math.sqrt(e / this.timeBuf.length);
    const target = clamp(rms * 3, 0, 1);
    const k = target > this.levelSmooth ? 0.5 : 0.12;
    this.levelSmooth += (target - this.levelSmooth) * k;
    return this.levelSmooth;
  }

  /** Byte frequency data from the post-master analyser. */
  getSpectrum(out: Uint8Array): void {
    const g = this.g;
    if (!g) {
      out.fill(0);
      return;
    }
    g.analyser.getByteFrequencyData(out as Uint8Array<ArrayBuffer>);
  }

  /** Frequency bin count of the analyser (size for getSpectrum). */
  get spectrumSize(): number {
    return this.g?.analyser.frequencyBinCount ?? 1024;
  }

  /** A MediaStream of the master output, for MediaRecorder. Requires start(). */
  createRecordingStream(): MediaStream {
    const g = this.g;
    const c = this.owned;
    if (!g || !c) throw new Error("AudioEngine: start() must be called before createRecordingStream()");
    if (!this.recordDest) {
      this.recordDest = c.createMediaStreamDestination();
      g.out.connect(this.recordDest);
    }
    return this.recordDest.stream;
  }

  async suspend(): Promise<void> {
    if (this.owned && this.owned.state === "running") await this.owned.suspend();
  }

  async resume(): Promise<void> {
    if (this.owned && this.owned.state !== "running" && this.owned.state !== "closed") await this.owned.resume();
  }
}
